import { useState } from "react";
import RandomQuote from "../../components/RandomQuote/RandomQuote";
import Notification from "../../components/Notification/Notification";
import Spinner from "../../components/Spinner/Spinner";
import BrandTitle from "../../components/BrandTitle/BrandTitle";

export default function EnterpriseSignupPage() {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    email: "",
    firstName: "",
    lastName: "",
    password: "",
  });
  const [confirmPassword, setConfirmPassword] = useState("");
  const [billingContact, setBillingContact] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
  });

  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [notification, setNotification] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleBillingChange = (e) => {
    setBillingContact({ ...billingContact, [e.target.name]: e.target.value });
  };

  const goToBillingInfo = (e) => {
    e.preventDefault();
    setError("");
    if (!form.firstName || !form.lastName || !form.email || !form.password) {
      setError("Please fill all required fields");
      return;
    }
    if (form.password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }
    // Pre-fill billing contact email with user email
    if (!billingContact.email) {
      setBillingContact({ ...billingContact, email: form.email });
    }
    setStep(2);
  };

  const handleBack = (e) => {
    e.preventDefault();
    setStep(1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccessMessage("");

    if (!termsAccepted) {
      setError(
        "Please accept the Terms of Service and Privacy Policy to continue",
      );
      return;
    }

    if (
      !billingContact.name ||
      !billingContact.email ||
      !billingContact.company
    ) {
      setError("Please fill all billing contact fields");
      return;
    }

    setIsLoading(true);

    try {
      const payload = {
        ...form,
        billingContact,
      };

      const res = await fetch(
        `${import.meta.env.VITE_API_URL}/api/auth/signup-enterprise`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        },
      );

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Signup failed");

      setSuccessMessage(
        "Enterprise account created! Check your email to verify your account. The link in the email will lead you back to login.",
      );
      setNotification({
        message: "Enterprise account created! Check your email to verify.",
        type: "success",
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="authentication-view">
      {isLoading && <Spinner text="... creating your enterprise account" />}
      {notification && (
        <Notification
          message={notification.message}
          type={notification.type}
          onClose={() => setNotification(null)}
        />
      )}
      <div className="left-wrapper">
        <div className="title">
          <BrandTitle fontSize={40} className="brand-title-login" />
        </div>
        <div className="quote">
          <RandomQuote />
        </div>
      </div>

      <div className="right-wrapper">
        {successMessage ? (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              textAlign: "center",
              padding: "40px",
              maxWidth: "500px",
              height: "100%",
              margin: "auto",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "15px",
                marginBottom: "30px",
              }}
            >
              <div
                style={{
                  fontSize: "48px",
                  color: "#6c5ce7",
                }}
              >
                ✓
              </div>
              <div
                className="title"
                style={{
                  margin: 0,
                  color: "#6c5ce7",
                }}
              >
                Success!
              </div>
            </div>
            <div
              style={{
                display: "inline-block",
                padding: "6px 12px",
                background: "rgba(108, 92, 231, 0.1)",
                color: "#6c5ce7",
                borderRadius: "6px",
                fontSize: "12px",
                fontWeight: 600,
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                marginBottom: "20px",
              }}
            >
              🏢 Enterprise Account
            </div>
            <p
              style={{
                fontSize: "16px",
                lineHeight: "1.6",
                color: "#ccc",
                marginBottom: "30px",
              }}
            >
              {successMessage}
            </p>
            <p>You can close this window now.</p>

            <p style={{ marginTop: "40px" }}>
              <a className="link" href="/login">
                Go to Login
              </a>
            </p>
          </div>
        ) : (
          <>
            <div className="title">
              <span
                style={{ display: "flex", alignItems: "center", gap: "10px" }}
              >
                <span>Enterprise Sign Up</span>
                <span
                  style={{
                    fontSize: "12px",
                    fontWeight: 600,
                    padding: "4px 8px",
                    background: "rgba(108, 92, 231, 0.2)",
                    color: "#6c5ce7",
                    borderRadius: "4px",
                  }}
                >
                  Invoice Billing
                </span>
              </span>
            </div>

            {step === 1 && (
              <form onSubmit={goToBillingInfo} autoComplete="off">
                <div className="auth-names">
                  <input
                    name="firstName"
                    className="flow-form-row"
                    value={form.firstName}
                    onChange={handleChange}
                    placeholder="First Name"
                    autoComplete="new-first-name"
                    required
                  />
                  <input
                    name="lastName"
                    className="flow-form-row"
                    value={form.lastName}
                    onChange={handleChange}
                    placeholder="Last Name"
                    autoComplete="new-last-name"
                    required
                  />
                </div>
                <br />
                <input
                  name="email"
                  className="flow-form"
                  type="email"
                  value={form.email}
                  onChange={handleChange}
                  placeholder="Your Email"
                  autoComplete="new-email"
                  required
                />
                <br />
                <input
                  name="password"
                  className="flow-form"
                  type="password"
                  value={form.password}
                  onChange={handleChange}
                  placeholder="Password"
                  autoComplete="new-password"
                  required
                />
                <br />
                <input
                  name="confirmPassword"
                  className="flow-form"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm Password"
                  autoComplete="new-confirm-password"
                  required
                />
                <br />
                {error && (
                  <span
                    style={{
                      color: "var(--error-color)",
                      fontSize: "14px",
                      display: "block",
                      marginBottom: "10px",
                    }}
                  >
                    {error}
                  </span>
                )}
                <button type="submit" className="flow-button">
                  Next: Billing Contact
                </button>
              </form>
            )}

            {step === 2 && (
              <form onSubmit={handleSubmit} autoComplete="off">
                <div
                  style={{
                    marginBottom: "20px",
                    fontSize: "14px",
                    color: "#999",
                  }}
                >
                  <p style={{ margin: "0 0 10px 0" }}>
                    <strong>Invoice Billing Information</strong>
                  </p>
                  <p style={{ margin: 0 }}>
                    Invoices will be sent to this contact each month.
                  </p>
                </div>

                <input
                  name="company"
                  className="flow-form"
                  value={billingContact.company}
                  onChange={handleBillingChange}
                  placeholder="Company/Organization Name *"
                  required
                />
                <br />
                <input
                  name="name"
                  className="flow-form"
                  value={billingContact.name}
                  onChange={handleBillingChange}
                  placeholder="Billing Contact Name *"
                  required
                />
                <br />
                <input
                  name="email"
                  className="flow-form"
                  type="email"
                  value={billingContact.email}
                  onChange={handleBillingChange}
                  placeholder="Billing Contact Email *"
                  required
                />
                <br />
                <input
                  name="phone"
                  className="flow-form"
                  type="tel"
                  value={billingContact.phone}
                  onChange={handleBillingChange}
                  placeholder="Billing Contact Phone (Optional)"
                />
                <br />

                {/* Terms and conditions */}
                <div
                  style={{
                    marginTop: "24px",
                    marginBottom: "20px",
                    fontSize: "14px",
                    lineHeight: "1.6",
                    color: "#999",
                  }}
                >
                  <p style={{ margin: "0 0 16px 0" }}>
                    <strong>Enterprise Billing:</strong> You'll be invoiced
                    monthly based on active members in your organization at $8
                    per member. Invoices are sent with 30-day payment terms.
                  </p>

                  <label
                    style={{
                      display: "flex",
                      alignItems: "flex-start",
                      cursor: "pointer",
                      gap: "10px",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={termsAccepted}
                      onChange={(e) => setTermsAccepted(e.target.checked)}
                      style={{
                        marginTop: "3px",
                        cursor: "pointer",
                        width: "16px",
                        height: "16px",
                        flexShrink: 0,
                      }}
                    />
                    <span>
                      I agree to the{" "}
                      <a
                        href="/legal/terms-of-service"
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          color: "#6c5ce7",
                          textDecoration: "underline",
                        }}
                      >
                        Terms of Service
                      </a>{" "}
                      and{" "}
                      <a
                        href="/legal/privacy-policy"
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          color: "#6c5ce7",
                          textDecoration: "underline",
                        }}
                      >
                        Privacy Policy
                      </a>
                      , and understand the enterprise billing terms to start a
                      14-day free trial.
                    </span>
                  </label>
                </div>

                {error && (
                  <span
                    style={{
                      color: "var(--error-color)",
                      fontSize: "14px",
                      display: "block",
                      marginBottom: "10px",
                    }}
                  >
                    {error}
                  </span>
                )}

                <div style={{ display: "flex", gap: "10px" }}>
                  <button
                    type="button"
                    onClick={handleBack}
                    className="flow-button"
                    style={{
                      background: "transparent",
                      border: "1px solid var(--border-color)",
                      color: "var(--main-text-color)",
                    }}
                  >
                    Back
                  </button>
                  <button
                    type="submit"
                    className="flow-button"
                    style={{ flex: 1, background: "#6c5ce7" }}
                  >
                    Create Enterprise Account
                  </button>
                </div>
              </form>
            )}

            {!successMessage && (
              <p style={{ marginTop: "20px" }}>
                Need a regular account?{" "}
                <a className="link" href="/signup">
                  Sign up here
                </a>
              </p>
            )}
          </>
        )}

        <div className="logo">
          <img
            src="/somigo_logo.svg"
            alt="SOMIGO Icon"
            width="100"
            height="100"
          />
        </div>
      </div>
    </div>
  );
}
